import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, QrCode, Upload, X, Download, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TicketDetails = () => {
  const { ticketId } = useParams();
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicket(response.data);
    } catch (error) {
      toast.error('Failed to fetch ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      fetchTicket();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;
        await axios.post(
          `${API}/tickets/${ticketId}/images`,
          { image: base64Image },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/x-www-form-urlencoded' } }
        );
        toast.success('Image uploaded successfully');
        fetchTicket();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${ticket.ticket_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      picked: 'bg-blue-100 text-blue-700',
      washed: 'bg-purple-100 text-purple-700',
      ready: 'bg-green-100 text-green-700',
      delivered: 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return <div className="text-center text-slate-500">Ticket not found</div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/tickets')} data-testid="back-to-tickets-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tickets
        </Button>
        <div className="flex gap-2">
          <Button
            onClick={handleDownloadInvoice}
            variant="outline"
            data-testid="download-invoice-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Invoice
          </Button>
          <Button
            onClick={() => navigate(`/tickets/${ticketId}/tag`)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="view-tag-btn"
          >
            <QrCode className="w-4 h-4 mr-2" />
            View Tag
          </Button>
        </div>
      </div>

      {/* Ticket Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2 font-mono">
                {ticket.ticket_number}
              </h1>
              <p className="text-lg text-slate-600">{ticket.customer_name}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500 mb-2">Total Amount</p>
              <p className="text-4xl font-bold text-indigo-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                ₹{ticket.total_price.toFixed(2)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Update - Super Admin Only */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Status
            {!isSuperAdmin() && (
              <Badge className="bg-amber-100 text-amber-700 text-xs flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Admin Only
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600">Current Status:</span>
            <Badge className={`${getStatusColor(ticket.status)} text-base px-4 py-1`}>
              {ticket.status}
            </Badge>
          </div>
          {isSuperAdmin() ? (
            <div className="space-y-2">
              <Label>Update Status</Label>
              <Select value={ticket.status} onValueChange={handleStatusUpdate}>
                <SelectTrigger data-testid="status-update-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="picked">Picked</SelectItem>
                  <SelectItem value="washed">Washed</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
            </Select>
          </div>
          ) : (
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                Only the Super Admin can change ticket status. Contact your administrator if a status update is needed.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ticket Details */}
      <Card>
        <CardHeader>
          <CardTitle>Ticket Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Service Type</p>
              <p className="text-base text-slate-900">{ticket.service_type}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Weight</p>
              <p className="text-base text-slate-900">{ticket.kg} kg</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Clothes</p>
              <p className="text-base text-slate-900">{ticket.clothes_count} items</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Pickup Date</p>
              <p className="text-base text-slate-900">
                {format(new Date(ticket.pickup_date), 'MMMM dd, yyyy')}
              </p>
            </div>
            {ticket.delivery_date && (
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Delivery Date</p>
                <p className="text-base text-slate-900">
                  {format(new Date(ticket.delivery_date), 'MMMM dd, yyyy')}
                </p>
              </div>
            )}
            {ticket.special_instructions && (
              <div className="md:col-span-2">
                <p className="text-sm font-medium text-slate-500 mb-1">Special Instructions</p>
                <p className="text-base text-slate-900">{ticket.special_instructions}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      {ticket.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Item</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Service</th>
                    <th className="text-center p-3 text-sm font-medium text-slate-600">Quantity</th>
                    <th className="text-right p-3 text-sm font-medium text-slate-600">Price/Unit</th>
                    <th className="text-right p-3 text-sm font-medium text-slate-600">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ticket.items.map((item, index) => (
                    <tr key={index} className="border-t border-slate-200">
                      <td className="p-3 text-sm">{item.item_name}</td>
                      <td className="p-3 text-sm">{item.service_type}</td>
                      <td className="p-3 text-sm text-center">{item.quantity}</td>
                      <td className="p-3 text-sm text-right">₹{item.price_per_unit.toFixed(2)}</td>
                      <td className="p-3 text-sm text-right font-medium">₹{item.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Images */}
      <Card>
        <CardHeader>
          <CardTitle>Clothes Images (2-week retention)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="image-upload">Upload Image</Label>
            <div className="mt-2">
              <Input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                data-testid="image-upload-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">Max file size: 5MB. Images will be automatically deleted after 2 weeks.</p>
          </div>
          {ticket.images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {ticket.images.map((image, index) => (
                <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                  <img src={image} alt={`Clothes ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
          {ticket.images.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Upload className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No images uploaded yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketDetails;
